import { Input, Component, EventEmitter, Output } from '@angular/core';
import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { SurveyUserTypes } from '../constants-types';
import { InvitationSource } from '../core-data-model';
import { Job } from '../core-data-model';
import { Person, PersonUpdate } from '../core-data-model';
import { AssessmentCenterService } from '../core-services';
import { ContextDataService } from '../core-services';
import { FoldersDataService } from '../core-services';
import { notification } from '../core-services';
import { PersonApi } from '../core-services';
import { EmailValidatorDirective, NotOnlyWhitespace } from '../core-ui';
import { TranslationService } from '../translations';

/**
 * @deprecated Create person with BA uses core and classic APIs and should be replaced with v5 endpoints
 */
@Component({
    selector: 'create-person-with-BA',
    template: `
        <div
            [ngClass]="{
                createPerson_container: isHireInvitationSource,
                'border-color-neutral-400': isHireInvitationSource,
                'bg-color-neutral-50': isHireInvitationSource
            }"
        >
            <div
                [formGroup]="personCreationForm"
                piTesting="create-person-with-ba"
            >
                <div class="layoutLastPushedRight-X">
                    <div class="form-group" style="margin-right:15px">
                        <label class="control-label">
                            <span class="required">{{
                                TR.key('label.common-terms.first.name')
                            }}</span>
                            <input
                                class="form-control input-sm"
                                type="text"
                                formControlName="firstName"
                                piTesting="search-or-create-person-first-name"
                            />
                        </label>
                    </div>
                    <div class="form-group">
                        <label class="control-label">
                            <span class="required">{{
                                TR.key('label.common-terms.last.name')
                            }}</span>
                            <input
                                class="form-control input-sm"
                                type="text"
                                formControlName="lastName"
                                piTesting="search-or-create-person-last-name"
                            />
                        </label>
                    </div>
                </div>
                <div class="form-group">
                    <label class="control-label" style="max-width:none">
                        <span class="required">{{
                            TR.key('label.common-terms.email')
                        }}</span>
                        <input
                            class="form-control input-sm"
                            type="text"
                            formControlName="email"
                            piTesting="search-or-create-person-email"
                        />
                    </label>
                </div>
                <div
                    [ngClass]="{
                        'right-primary-button': SendAssessmentButtonOnRightSide
                    }"
                >
                    <button
                        class="button button-sm button-primary"
                        [disabled]="this.personCreationForm.invalid"
                        [ngClass]="{
                            'button--loading ': isCreatingPerson
                        }"
                        [ngStyle]="{
                            'margin-right': !SendAssessmentButtonOnRightSide
                                ? '10px'
                                : ''
                        }"
                        (click)="createPersonAndSendAssessment()"
                        piTesting="search-or-create-person-submit-button"
                    >
                        {{ sendAssessmentBtnText }}
                    </button>
                </div>
                @if (showCloseButton) {
                    <button
                        class="button button-sm button-secondary"
                        style="flex-grow:0"
                        [disabled]="isCreatingPerson"
                        (click)="close.emit()"
                    >
                        {{ TR.key('text.common-terms.cancel') }}
                    </button>
                }
            </div>
        </div>
    `,
    styles: [
        `
            .createPerson_container {
                padding: 20px 30px 30px 30px;
                border-width: 1px;
                border-style: solid;
                border-radius: 5px;
            }
            .right-primary-button {
                display: flex;
                justify-content: flex-end;
            }
        `
    ],
    standalone: false
})
export class CreatePersonWithBAComponent {
    @Input()
    invitationSource: InvitationSource;
    @Input()
    showCloseButton = true;
    @Input()
    userType: SurveyUserTypes = SurveyUserTypes.Employee;
    @Input()
    job: Job;
    @Input()
    SendAssessmentButtonOnRightSide = false;
    @Input()
    sendButtonTextKey: string;
    @Output()
    close = new EventEmitter();

    @Output()
    creatingPerson = new EventEmitter<boolean>();

    @Output()
    createdPerson = new EventEmitter<Person>();

    personCreationForm: UntypedFormGroup;
    isCreatingPerson = false;

    constructor(
        public TR: TranslationService,
        private folderService: FoldersDataService,
        private context: ContextDataService,
        private assessmentCenterService: AssessmentCenterService,
        private personApi: PersonApi
    ) {
        this.personCreationForm = new UntypedFormGroup({
            firstName: new UntypedFormControl('', [
                Validators.required,
                NotOnlyWhitespace,
                Validators.minLength(2),
                Validators.maxLength(50)
            ]),
            lastName: new UntypedFormControl('', [
                Validators.required,
                NotOnlyWhitespace,
                Validators.minLength(2),
                Validators.maxLength(50)
            ]),
            email: new UntypedFormControl('', [
                Validators.required,
                EmailValidatorDirective.emailValidation,
                Validators.maxLength(510)
            ])
        });
    }

    get isHireInvitationSource(): boolean {
        return this.invitationSource === InvitationSource.Hire;
    }

    get sendAssessmentBtnText(): string {
        if (this.sendButtonTextKey) {
            return this.TR.key(this.sendButtonTextKey);
        }

        return this.TR.key('sendassessmenttitle');
    }

    @notification(
        'onboarding.emailsuccess-title',
        'onboarding.emailsuccess-message'
    )
    async createPersonAndSendAssessment() {
        try {
            this.setIsCreatingPerson(true);
            const userToCreate = new PersonUpdate({
                email: this.personCreationForm.get('email').value,
                firstName: this.personCreationForm.get('firstName').value,
                lastName: this.personCreationForm.get('lastName').value,
                type: this.userType,
                folder: this.folderService.defaultFolder
            });

            if (this.job) {
                userToCreate.job = this.job;
                userToCreate.folder = this.job.folder;
            }

            // Create person
            const newPerson =
                await this.personApi.createNewPerson(userToCreate);

            this.context.addToResultSet(newPerson);
            // Send BA to person
            await this.assessmentCenterService.sendEmail(
                false,
                this.invitationSource,
                false,
                null
            );
            this.createdPerson.emit(newPerson);
            this.personCreationForm.reset();
        } catch (e) {
            // Not doing anything here because we will have an Error Toast if this fails and we would throw a log on the console anyway
        } finally {
            this.setIsCreatingPerson(false);
            this.context.clearResultSet();
            this.close.emit();
        }
    }

    setIsCreatingPerson(isCreating: boolean) {
        this.isCreatingPerson = isCreating;
        this.creatingPerson.emit(isCreating);
    }
}
